import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import DentalAutomationSection from '../pages/DentalAutomationSection';
import MDeRCMDentalComponent from '../pages/MDeRCMDentalComponent';
import ConsultationCTA from '../components/ConsultationCTA';

// Improved Dental RCM Visual Component (Integrated)
const ImprovedDentalRCMVisual = () => {
  const [activeStep, setActiveStep] = useState(0);
const [particlesVisible, _setParticlesVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const steps = [
    { 
      icon: '✓', 
      label: 'Verified', 
      sublabel: 'Eligible',
      color: '#10b981',
      bgColor: 'rgba(16, 185, 129, 0.1)',
      position: { top: '15%', left: '10%' }
    },
    { 
      icon: '$', 
      label: 'Insurance', 
      sublabel: 'Locked',
      color: '#3b82f6',
      bgColor: 'rgba(59, 130, 246, 0.1)',
      position: { top: '15%', right: '10%' }
    },
    { 
      icon: '📄', 
      label: 'Claims', 
      sublabel: 'Filed',
      color: '#8b5cf6',
      bgColor: 'rgba(139, 92, 246, 0.1)',
      position: { bottom: '15%', left: '10%' }
    },
    { 
      icon: '⚡', 
      label: 'Revenue', 
      sublabel: 'Posted',
      color: '#f97316',
      bgColor: 'rgba(249, 115, 22, 0.1)',
      position: { bottom: '15%', right: '10%' }
    }
  ];

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      maxWidth: '600px',
      height: '500px',
      margin: '0 auto'
    }}>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
        }
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes ripple {
          0% { transform: scale(0.8); opacity: 1; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .dental-particle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: radial-gradient(circle, rgba(59, 130, 246, 0.8), transparent);
          border-radius: 50%;
          animation: float 3s ease-in-out infinite;
        }
        .dental-tooth-container {
          animation: float 6s ease-in-out infinite;
        }
        .dental-step-card {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .dental-step-card:hover {
          transform: translateY(-8px) scale(1.05) !important;
        }
        .dental-ai-badge {
          animation: pulse 2s ease-in-out infinite;
        }
        .dental-automated-badge {
          animation: shimmer 3s linear infinite;
          background: linear-gradient(
            90deg,
            rgba(16, 185, 129, 0.1) 0%,
            rgba(16, 185, 129, 0.3) 50%,
            rgba(16, 185, 129, 0.1) 100%
          );
          background-size: 1000px 100%;
        }
      `}</style>

      {/* Main Container */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '24px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {/* Animated Particles */}
        {particlesVisible && [...Array(20)].map((_, i) => (
          <div
            key={i}
            className="dental-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 3}s`
            }}
          />
        ))}

        {/* Inner Content */}
        <div style={{
          position: 'relative',
          margin: '20px',
          height: 'calc(100% - 40px)',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: 'inset 0 2px 10px rgba(0, 0, 0, 0.05)'
        }}>
          {/* Automated Badge */}
          <div className="dental-automated-badge" style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            padding: '8px 16px',
            background: 'rgba(16, 185, 129, 0.1)',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            zIndex: 20,
            border: '1px solid rgba(16, 185, 129, 0.3)',
            boxShadow: '0 4px 15px rgba(16, 185, 129, 0.2)'
          }}>
            <div style={{
              width: '8px',
              height: '8px',
              background: '#10b981',
              borderRadius: '50%',
              animation: 'pulse 2s ease-in-out infinite'
            }} />
            <span style={{
              fontSize: '13px',
              fontWeight: '600',
              color: '#10b981',
              letterSpacing: '0.5px'
            }}>
              Automated
            </span>
          </div>

          {/* Central Tooth */}
          <div className="dental-tooth-container" style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10
          }}>
            {/* Rotating Ring */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '160px',
              height: '160px',
              border: '3px dashed rgba(139, 92, 246, 0.3)',
              borderRadius: '50%',
              animation: 'rotate 20s linear infinite'
            }} />

            {/* Pulsing Circle */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '120px',
              height: '120px',
              background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2), transparent)',
              borderRadius: '50%',
              animation: 'pulse 3s ease-in-out infinite'
            }} />

            {/* Tooth Icon */}
            <div style={{
              fontSize: '64px',
              textAlign: 'center',
              filter: 'drop-shadow(0 10px 20px rgba(0, 0, 0, 0.15))',
              position: 'relative',
              zIndex: 5
            }}>
              🦷
            </div>
          </div>

          {/* AI Processing */}
          <div className="dental-ai-badge" style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, 80px)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 14px',
            background: 'rgba(139, 92, 246, 0.1)',
            borderRadius: '20px',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            zIndex: 15
          }}>
            <div style={{
              width: '6px',
              height: '6px',
              background: '#8b5cf6',
              borderRadius: '50%'
            }} />
            <div style={{
              width: '6px',
              height: '6px',
              background: '#8b5cf6',
              borderRadius: '50%',
              animationDelay: '0.2s'
            }} />
            <div style={{
              width: '6px',
              height: '6px',
              background: '#8b5cf6',
              borderRadius: '50%',
              animationDelay: '0.4s'
            }} />
            <span style={{
              fontSize: '12px',
              fontWeight: '600',
              color: '#8b5cf6',
              marginLeft: '4px'
            }}>
              AI Processing
            </span>
          </div>

          {/* Step Cards */}
          {steps.map((step, index) => (
            <div
              key={index}
              className="dental-step-card"
              style={{
                position: 'absolute',
                ...step.position,
                padding: '16px 20px',
                background: activeStep === index 
                  ? `linear-gradient(135deg, ${step.color}15, ${step.color}25)`
                  : step.bgColor,
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                border: `2px solid ${activeStep === index ? step.color : 'rgba(255, 255, 255, 0.5)'}`,
                boxShadow: activeStep === index 
                  ? `0 12px 30px ${step.color}40, 0 0 0 4px ${step.color}15`
                  : '0 8px 20px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                zIndex: activeStep === index ? 15 : 10,
                minWidth: '110px',
                transform: activeStep === index ? 'scale(1.1)' : 'scale(1)'
              }}
            >
              {activeStep === index && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '100%',
                  height: '100%',
                  border: `2px solid ${step.color}`,
                  borderRadius: '16px',
                  animation: 'ripple 2s ease-out infinite',
                  pointerEvents: 'none'
                }} />
              )}

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  fontSize: '28px',
                  filter: `drop-shadow(0 4px 8px ${step.color}40)`,
                  animation: activeStep === index ? 'bounce 1s ease-in-out infinite' : 'none'
                }}>
                  {step.icon}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#1f2937',
                  textAlign: 'center'
                }}>
                  {step.label}
                </div>
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: step.color,
                  textAlign: 'center',
                  padding: '2px 8px',
                  background: `${step.color}20`,
                  borderRadius: '8px'
                }}>
                  {step.sublabel}
                </div>
              </div>
            </div>
          ))}

          {/* Connection Lines */}
          <svg style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 5
          }}>
            {steps.map((step, index) => {
              const centerX = 50;
              const centerY = 50;
              const stepX = step.position.left ? 20 : 80;
              const stepY = step.position.top ? 25 : 75;
              
              return (
                <line
                  key={index}
                  x1={`${centerX}%`}
                  y1={`${centerY}%`}
                  x2={`${stepX}%`}
                  y2={`${stepY}%`}
                  stroke={activeStep === index ? step.color : 'rgba(139, 92, 246, 0.2)'}
                  strokeWidth={activeStep === index ? '3' : '2'}
                  strokeDasharray={activeStep === index ? '0' : '5,5'}
                  style={{
                    transition: 'all 0.3s ease'
                  }}
                />
              );
            })}
          </svg>

          {/* Bottom Bar */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '20px',
            background: 'linear-gradient(to top, rgba(255, 255, 255, 0.95), transparent)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 20
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                fontSize: '24px',
                animation: 'pulse 2s ease-in-out infinite'
              }}>
                🦷
              </div>
              <div>
                <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>
                  Dental RCM
                </div>
              </div>
            </div>

            <div style={{
              fontSize: '14px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #10b981, #06b6d4)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              MDeRCM
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '20px' }}>⚡</span>
              <div>
                <div style={{ 
                  fontSize: '13px', 
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #f97316, #dc2626)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  98% Automated
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Corner Elements */}
        <div style={{
          position: 'absolute',
          top: '-50px',
          left: '-50px',
          width: '100px',
          height: '100px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.2), transparent)',
          borderRadius: '50%',
          filter: 'blur(30px)'
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-50px',
          right: '-50px',
          width: '100px',
          height: '100px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.2), transparent)',
          borderRadius: '50%',
          filter: 'blur(30px)'
        }} />
      </div>

      {/* RPA Badge */}
      <div style={{
        position: 'absolute',
        bottom: '-60px',
        left: '20px',
        background: 'white',
        padding: '16px 24px',
        borderRadius: '16px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        zIndex: 25
      }}>
        <div style={{
          fontSize: '32px',
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          padding: '8px',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          🤖
        </div>
        <div>
          <div style={{
            fontSize: '18px',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            RPA
          </div>
          <div style={{
            fontSize: '12px',
            color: '#6b7280',
            fontWeight: '500'
          }}>
            Smart Dental
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Dental Component
const Dental = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #e0e7ff 100%)',
    overflow: 'hidden',
    position: 'relative',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  };

  const backgroundElementStyle1 = {
    position: 'absolute',
    top: '-10rem',
    right: '-10rem',
    width: '20rem',
    height: '20rem',
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(139, 92, 246, 0.2))',
    borderRadius: '50%',
    filter: 'blur(60px)',
    animation: 'pulse 3s ease-in-out infinite'
  };

  const backgroundElementStyle2 = {
    position: 'absolute',
    bottom: '-10rem',
    left: '-10rem',
    width: '20rem',
    height: '20rem',
    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(59, 130, 246, 0.2))',
    borderRadius: '50%',
    filter: 'blur(60px)',
    animation: 'pulse 3s ease-in-out infinite',
    animationDelay: '1s'
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Dental Revenue Cycle Management | Best RCM | MDeRCM</title>
        <meta 
          name="description" 
          content="AI-powered dental RCM services. Expert dental billing outsourcing, claim management and denial prevention for dental practices." 
        />
        <meta 
          name="keywords" 
          content="dental revenue cycle management, dental billing services, healthcare revenue cycle management services, medical revenue cycle management services, revenue cycle management consulting services, medical billing outsourcing services, best revenue cycle management companies, revenue cycle management services usa, hospital revenue cycle management, healthcare cost reduction strategies, how to improve revenue cycle management, outsourced revenue cycle management services, revenue cycle management services for hospitals, medical billing and coding outsourcing, revenue cycle management services companies" 
        />
        <link rel="canonical" href="https://mdercm.com/Dental" />
        <meta property="og:title" content="Dental Revenue Cycle Management Services | AI-Powered RCM" />
        <meta property="og:description" content="Transform your dental practice with AI-powered revenue cycle management. 98% automation, 40% cost reduction, 30% revenue increase." />
        <meta property="og:url" content="https://mdercm.com/Dental" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://mdercm.com/assets/dental-rcm-og.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dental Revenue Cycle Management Services" />
        <meta name="twitter:description" content="98% automation with 40% cost reduction for dental practices." />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.8; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.1); }
        }
        @media (max-width: 768px) {
          .main-content-dental {
            grid-template-columns: 1fr !important;
            padding: 2rem 1rem !important;
            gap: 2rem !important;
          }
        }
      `}</style>

      <div style={containerStyle}>
        <div style={backgroundElementStyle1}></div>
        <div style={backgroundElementStyle2}></div>

        {/* HERO SECTION */}
        <div className="main-content-dental" style={{
          position: 'relative',
          zIndex: 10,
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '4rem 1.5rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
          gap: '4rem',
          alignItems: 'center'
        }}>
          {/* Left Content */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem',
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateX(0)' : 'translateX(-50px)',
            transition: 'all 1s ease-out'
          }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.5rem 1rem',
              background: 'linear-gradient(135deg, #ef4444, #ec4899)',
              color: 'white',
              fontSize: '0.875rem',
              fontWeight: '600',
              borderRadius: '9999px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
              width: 'fit-content'
            }}>
              ⚡ AI-Powered Dental RCM
            </div>

            <h1 style={{
              fontSize: 'clamp(2.5rem, 5vw, 4rem)',
              fontWeight: '700',
              color: '#111827',
              lineHeight: '1.1',
              margin: '1rem 0'
            }}>
              Transform Your <span style={{
                background: 'linear-gradient(135deg, #2563eb, #8b5cf6)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>Dental Practice</span> with AI Revenue Cycle Management
            </h1>

            <p style={{
              fontSize: '1.125rem',
              color: '#6b7280',
              lineHeight: '1.6',
              marginBottom: '1rem'
            }}>
              Leading <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>revenue cycle management services company</Link> specializing in dental practices. 
              Our <strong>healthcare revenue cycle management services</strong> automate eligibility, claims, and payments with proven <strong>healthcare cost reduction strategies</strong>.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1.5rem',
              textAlign: 'center',
              marginTop: '1rem'
            }}>
              <div>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #10b981, #06b6d4)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '0.5rem'
                }}>
                  98%
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Automation Rate</div>
              </div>
              <div>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '0.5rem'
                }}>
                  40%
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Cost Reduction</div>
              </div>
              <div>
                <div style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #ef4444, #ec4899)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  marginBottom: '0.5rem'
                }}>
                  30%
                </div>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>Revenue Increase</div>
              </div>
            </div>

            <Link 
              to="/free-trial" 
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '1rem 2rem',
                background: 'linear-gradient(135deg, #ef4444, #ec4899)',
                color: 'white',
                fontWeight: '600',
                borderRadius: '9999px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
                border: 'none',
                transition: 'all 0.3s ease',
                marginTop: '1rem',
                textDecoration: 'none',
                width: 'fit-content'
              }}
              onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
            >
              Start Your Free Trial →
            </Link>
          </div>

          {/* Right Content - Improved Visual */}
          <div style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateX(0)' : 'translateX(50px)',
            transition: 'all 1s ease-out 0.3s'
          }}>
            <ImprovedDentalRCMVisual />
          </div>
        </div>

        {/* RELATED SERVICES SECTION - 4x2 GRID */}
        <div style={{ 
          padding: '80px 5%', 
          backgroundColor: '#f7f9fb',
          position: 'relative',
          zIndex: 10
        }}>
          <style>{`
            .dental-services-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 30px;
              max-width: 1400px;
              margin: 0 auto;
            }
            @media (max-width: 1200px) {
              .dental-services-grid {
                grid-template-columns: repeat(3, 1fr);
              }
            }
            @media (max-width: 900px) {
              .dental-services-grid {
                grid-template-columns: repeat(2, 1fr);
              }
            }
            @media (max-width: 600px) {
              .dental-services-grid {
                grid-template-columns: 1fr;
              }
            }
          `}</style>

          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '30px',
            color: '#001F2B'
          }}>
            Medical Billing Outsourcing Services - Complete Dental RCM Solutions
          </h2>

          <p style={{ 
            textAlign: 'center', 
            fontSize: '17px', 
            color: '#555', 
            marginBottom: '60px',
            maxWidth: '900px',
            margin: '0 auto 60px'
          }}>
            Explore our comprehensive <strong>medical billing outsourcing services</strong> designed specifically for dental practices to optimize every aspect of your revenue cycle.
          </p>

          <div className="dental-services-grid">
            {[
              {
                icon: '💳',
                title: 'Payment Posting Services',
                desc: 'Automated payment posting with 99%+ accuracy for faster reconciliation and optimized cash flow.',
                link: '/healthcare/ai/payment-posting'
              },
              {
                icon: '📊',
                title: 'Accounts Receivable',
                desc: 'AI-powered AR automation reducing outstanding receivables by 60% and accelerating collections by 70%.',
                link: '/healthcare/ai/accounts-receivable'
              },
              {
                icon: '🎯',
                title: 'Denial Management',
                desc: 'Intelligent denial management with automated prevention, tracking, and appeals for maximum recovery.',
                link: '/healthcare/ai/denial-management'
              },
              {
                icon: '🔍',
                title: 'Compliance Management',
                desc: 'Automated compliance monitoring ensuring regulatory adherence, audit readiness, and risk reduction.',
                link: '/healthcare/ai/compliance-agent'
              },
              {
                icon: '💼',
                title: 'Eligibility Verification',
                desc: 'Real-time insurance eligibility checking that reduces denials by 95% and streamlines patient intake.',
                link: '/healthcare/ai/eligibility-check'
              },
              {
                icon: '📞',
                title: 'Patient Intake',
                desc: 'Automated patient intake improving data accuracy, reducing wait times, and enhancing patient experience.',
                link: '/healthcare/ai/patient-intake'
              },
              {
                icon: '📋',
                title: 'Prior Authorization',
                desc: 'Streamlined prior authorization processing accelerates approvals and reduces treatment delays.',
                link: '/healthcare/ai/prior-authorization'
              },
              {
                icon: '📄',
                title: 'Policy Verification',
                desc: 'Comprehensive policy status verification prevents coverage-related denials and billing errors.',
                link: '/healthcare/ai/policy-status-verification'
              }
            ].map((service, i) => (
              <article
                key={i}
                style={{
                  backgroundColor: '#ffffff',
                  padding: '30px',
                  borderRadius: '16px',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}
              >
                <div style={{ fontSize: '40px', marginBottom: '15px', color: '#F26522' }}>{service.icon}</div>
                <h3 style={{ 
                  fontSize: '20px', 
                  fontWeight: '700', 
                  color: '#001F2B', 
                  marginBottom: '12px' 
                }}>
                  {service.title}
                </h3>
                <p style={{ 
                  fontSize: '15px', 
                  color: '#555', 
                  lineHeight: '1.6',
                  marginBottom: '20px',
                  flex: 1
                }}>
                  {service.desc}
                </p>
                <Link 
                  to={service.link}
                  style={{ 
                    color: '#F26522', 
                    textDecoration: 'none', 
                    fontWeight: '600',
                    fontSize: '16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'gap 0.3s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.gap = '12px'}
                  onMouseOut={(e) => e.currentTarget.style.gap = '8px'}
                >
                  Learn More →
                </Link>
              </article>
            ))}
          </div>
        </div>

        {/* WHY CHOOSE MDERCM SECTION - 4x2 GRID */}
        <div style={{ 
          padding: '80px 5%', 
          backgroundColor: '#001F2B',
          color: '#ffffff',
          position: 'relative',
          zIndex: 10
        }}>
          <style>{`
            .dental-reasons-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 30px;
              max-width: 1400px;
              margin: 0 auto;
            }
            @media (max-width: 1200px) {
              .dental-reasons-grid {
                grid-template-columns: repeat(3, 1fr);
              }
            }
            @media (max-width: 900px) {
              .dental-reasons-grid {
                grid-template-columns: repeat(2, 1fr);
              }
            }
            @media (max-width: 600px) {
              .dental-reasons-grid {
                grid-template-columns: 1fr;
              }
            }
          `}</style>

          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(26px, 4vw, 38px)',
            fontWeight: '800',
            marginBottom: '30px',
            color: '#ffffff'
          }}>
            Why Choose MDeRCM for Dental Revenue Cycle Management?
          </h2>

          <p style={{ 
            textAlign: 'center', 
            fontSize: '17px', 
            color: '#dddddd', 
            marginBottom: '60px',
            maxWidth: '900px',
            margin: '0 auto 60px',
            lineHeight: '1.8'
          }}>
            As one of the <strong>best revenue cycle management companies</strong>, <Link to="/" style={{ color: '#F26522', textDecoration: 'none', fontWeight: '600' }}>MDeRCM</Link> provides industry-leading 
            <strong> revenue cycle management services USA</strong> with proven results and dedicated support for dental practices nationwide.
          </p>

          <div className="dental-reasons-grid">
            {[
              {
                icon: '🏆',
                title: 'Industry-Leading Expertise',
                desc: 'Over 10+ years specializing in dental revenue cycle management with proven success across thousands of practices.'
              },
              {
                icon: '🤖',
                title: 'Advanced AI Technology',
                desc: 'Cutting-edge AI and machine learning delivering 98% automation rates and 99.5% accuracy in claims processing.'
              },
              {
                icon: '💵',
                title: 'Maximize Revenue',
                desc: 'Increase collections by up to 30% through optimized workflows, faster processing, and proactive denial prevention.'
              },
              {
                icon: '⚡',
                title: '24/7 Support',
                desc: 'Round-the-clock dedicated support team ensuring seamless operations and quick resolution of any issues.'
              },
              {
                icon: '🔒',
                title: 'HIPAA Compliant',
                desc: 'Fully compliant with HIPAA and industry regulations, ensuring data security and patient privacy protection.'
              },
              {
                icon: '📈',
                title: 'Scalable Solutions',
                desc: 'Flexible services that grow with your practice, from solo practitioners to large multi-location dental groups.'
              },
              {
                icon: '💡',
                title: 'Transparent Reporting',
                desc: 'Real-time dashboards and detailed analytics providing complete visibility into your revenue cycle performance.'
              },
              {
                icon: '🎯',
                title: 'Proven ROI',
                desc: 'Most dental practices see positive ROI within 3-6 months with average cost reduction of 40% and revenue increase of 30%.'
              },
            ].map((reason, i) => (
              <article
                key={i}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  padding: '35px 25px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  transition: 'all 0.3s ease'
                }}
              >
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>{reason.icon}</div>
                <h3 style={{ 
                  fontSize: '20px', 
                  fontWeight: '700', 
                  color: '#ffffff', 
                  marginBottom: '12px' 
                }}>
                  {reason.title}
                </h3>
                <p style={{ 
                  fontSize: '15px', 
                  color: '#dddddd', 
                  lineHeight: '1.6'
                }}>
                  {reason.desc}
                </p>
              </article>
            ))}
          </div>

          <div style={{
            textAlign: 'center',
            marginTop: '60px'
          }}>
            <Link 
              to="/free-trial"
              style={{
                backgroundColor: '#F26522',
                color: '#ffffff',
                padding: '18px 50px',
                borderRadius: '8px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '18px',
                display: 'inline-block',
                transition: 'all 0.3s ease',
                boxShadow: '0 6px 20px rgba(242, 101, 34, 0.4)'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#d94d12';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = '#F26522';
                e.target.style.transform = 'scale(1)';
              }}
            >
              Start Your Free Trial Today
            </Link>
          </div>
        </div>

        <DentalAutomationSection /> 
        <MDeRCMDentalComponent /> 
        <ConsultationCTA /> 
      </div>
    </>
  );
};

export default Dental;